import { createContext, useEffect, useState } from "react";

import parseCookie from "utils/parseCookie";

export type CookieConsentContextProps = { consent: CookieConsent } | null;

export const CookieConsentContext = createContext<CookieConsentContextProps>(null);

export type CookieConsent =
    | {
          consentId: string | null;
          hasConsented: boolean;
          hasMadeAction: boolean;
          functional?: boolean;
          necessary?: boolean;
          other?: boolean;
          performance?: boolean;
          advertisement?: boolean;
          analytics?: boolean;
      }
    | Record<string, boolean>;

const CookieConsentProvider = ({ children }: { children: React.ReactNode }) => {
    const [consent, setConsent] = useState<CookieConsent>({
        consentId: null,
        hasConsented: false,
        hasMadeAction: false,
    });

    const syncCookieData = () => {
        try {
            if (typeof document?.cookie !== "undefined") {
                const { ["cookieyes-consent"]: cookieYes } = parseCookie(document.cookie);

                const entries = cookieYes?.split(",");

                if (entries?.length) {
                    let tempConsent: CookieConsent = {
                        consentId: entries?.[0].split(":")[1] ?? "",
                        hasConsented: entries?.[1].split(":")?.[1] === "yes",
                        hasMadeAction: entries?.[2].split(":")?.[1] === "yes",
                    };

                    for (let index = 3; index < entries.length; index++) {
                        const element: string[] = entries[index].split(":");
                        if (element.length > 1) {
                            const key: string = element[0];
                            const value: boolean = element[1] === "yes";
                            const tempEntry = { [key]: value };
                            tempConsent = {
                                ...tempConsent,
                                ...tempEntry,
                            };
                        }
                    }

                    setConsent(tempConsent);
                }
            }
        } catch (_error) {
            console.error("Could not map consent from cookies");
        }
    };

    useEffect(() => {
        syncCookieData();
    }, []);

    useEffect(() => {
        const eventUpdateListener = () => {
            syncCookieData();
        };

        document.addEventListener("cookieyes_consent_update", eventUpdateListener);

        return () => {
            document.removeEventListener("cookieyes_consent_update", eventUpdateListener);
        };
    }, []);

    return (
        <CookieConsentContext.Provider value={{ consent }}>
            {children}
        </CookieConsentContext.Provider>
    );
};

export default CookieConsentProvider;
